import React from 'react';
import { Typography, Badge, Button, Upload, Tag, Alert } from 'antd';
import { UploadOutlined, CheckCircleOutlined } from '@ant-design/icons';
import type { UploadProps } from 'antd';
import { MigrationFilters, RawSku } from '../types';
import SkuFilterBar from '../components/SkuFilterBar';
import SkuSelectionTable from '../components/SkuSelectionTable';
import { CONVERSION_DESCRIPTIONS } from '../constants';
import { notify } from '../../../utils/notify';

const { Text, Paragraph } = Typography;

interface Props {
  loading:            boolean;
  filters:            MigrationFilters;
  setFilters:         (f: MigrationFilters) => void;
  filteredSkus:       RawSku[];
  selectedSkuIds:     number[];
  toggleSkuSelection: (id: number) => void;
  toggleSelectAll:    (ids: number[]) => void;
  importCsv:          (file: File) => Promise<{ matched: number; unmatched: string[]; wrongModel: string[] }>;
  csvImported:        boolean;
}

const StepSelectSkus: React.FC<Props> = ({
  loading,
  filters,
  setFilters,
  filteredSkus,
  selectedSkuIds,
  toggleSkuSelection,
  toggleSelectAll,
  importCsv,
  csvImported,
}) => {
  const description = CONVERSION_DESCRIPTIONS[filters.conversionType];
  const isConversionSelected = filters.conversionType !== 'none';

  const showSelectConversionWarning = () => {
    notify.warn('Conversion Type Required', 'Please select Convertion Type before selecting SKUs or importing CSV.');
  };

  const guardedToggleSkuSelection = (id: number) => {
    if (!isConversionSelected) {
      showSelectConversionWarning();
      return;
    }
    toggleSkuSelection(id);
  };

  const guardedToggleSelectAll = (ids: number[]) => {
    if (!isConversionSelected) {
      showSelectConversionWarning();
      return;
    }
    toggleSelectAll(ids);
  };

  const uploadProps: UploadProps = {
    accept: '.csv',
    showUploadList: false,
    fileList: [],  // always empty — prevents "same file" being ignored on re-select
    beforeUpload: (file) => {
      if (!isConversionSelected) {
        showSelectConversionWarning();
        return false;
      }

      importCsv(file)
        .then(({ matched, unmatched, wrongModel }) => {
          notify.csvImportResult({ matched, unmatched, wrongModel });
        })
        .catch(() => {
          notify.error('CSV Read Failed', 'Could not read the file. Make sure it is a valid CSV.');
        });
      return false;
    },
  };

  return (
    <div>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        // message="What you can do from here"
        message="Create new SKUs with the same configuration using Clone, EA → ALC, or ALC → EA migrations."
        description="CSV import supports only one conversion at time whether it's EA → ALC or ALC → EA or Clone, before CSV import will have to select conversion type."
      />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
        <div style={{ margin: 0, flex: 1 }}>
          <Paragraph type="secondary" style={{ margin: 0 }}>
            {description}
          </Paragraph>
        </div>

        <Upload {...uploadProps}>
          <Button
            icon={csvImported ? <CheckCircleOutlined /> : <UploadOutlined />}
            type={csvImported ? 'default' : 'dashed'}
            style={{ marginLeft: 16, whiteSpace: 'nowrap' }}
            disabled={!isConversionSelected}
          >
            {csvImported ? 'CSV Imported' : 'Import CSV'}
          </Button>
        </Upload>
      </div>

      {csvImported && (
        <div style={{ marginBottom: 12 }}>
          <Tag color="green" icon={<CheckCircleOutlined />}>
            CSV applied — target names pre-filled on next screen
          </Tag>
        </div>
      )}

      <SkuFilterBar filters={filters} setFilters={setFilters} />

      <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'space-between' }}>
        <Text type="secondary">
          {filteredSkus.length} SKU{filteredSkus.length !== 1 ? 's' : ''} shown
        </Text>
        {selectedSkuIds.length > 0 && (
          <Badge count={selectedSkuIds.length} color="#1890ff">
            <Text style={{ paddingRight: 8 }}>selected</Text>
          </Badge>
        )}
      </div>

      <SkuSelectionTable
        loading={loading}
        skus={filteredSkus}
        selectedSkuIds={selectedSkuIds}
        toggleSkuSelection={guardedToggleSkuSelection}
        toggleSelectAll={guardedToggleSelectAll}
      />
    </div>
  );
};

export default StepSelectSkus;
